/**
 * Submit Content Component
 * Client-side form for community submissions
 */

'use client';

import { Suspense, useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAuth } from '@/lib/auth-context';
import { trackSubmission } from '@/lib/analytics';
import { functions, httpsCallable } from '@/lib/firebase.client';

interface OgData {
  ogTitle?: string | null;
  ogDescription?: string | null;
  ogImage?: string | null;
  ogUrl?: string | null;
  ogSiteName?: string | null;
  favicon?: string | null;
}

function SubmitFormContent() {
  const searchParams = useSearchParams();
  const { user } = useAuth();
  const prefill = searchParams.get('prefill') || '';

  const [formData, setFormData] = useState({
    title: prefill,
    description: '',
    productUrl: '',
    accountType: 'FSA' as 'FSA' | 'HSA' | 'Both',
    category: '',
    reasoning: '',
    availableUntil: '', // Optional availability date
    sources: [{ label: '', url: '' }],
  });

  const [ogData, setOgData] = useState<OgData | null>(null);
  const [fetchingOg, setFetchingOg] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Fetch OG data when product URL changes (debounced)
  useEffect(() => {
    const fetchOgData = async () => {
      if (!formData.productUrl || !isValidUrl(formData.productUrl)) {
        setOgData(null);
        return;
      }

      setFetchingOg(true);
      try {
        const scrapeOgCallable = httpsCallable(functions, 'scrapeOpenGraph');
        const response = await scrapeOgCallable({ url: formData.productUrl });
        const data = response.data as { success: boolean; data: OgData };
        if (data.success && data.data) {
          setOgData(data.data);
          // Auto-fill title if empty
          if (!formData.title && data.data.ogTitle) {
            setFormData(prev => ({ ...prev, title: data.data.ogTitle || '' }));
          }
          // Auto-fill description if empty
          if (!formData.description && data.data.ogDescription) {
            setFormData(prev => ({ ...prev, description: data.data.ogDescription || '' }));
          }
        }
      } catch (err) {
        console.error('Error fetching OG data:', err);
      } finally {
        setFetchingOg(false);
      }
    };

    const debounce = setTimeout(fetchOgData, 800);
    return () => clearTimeout(debounce);
  }, [formData.productUrl]);

  const isValidUrl = (str: string) => {
    try {
      new URL(str);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Filter out empty sources
      const validSources = formData.sources.filter((s) => s.label && s.url);

      const createSubmissionCallable = httpsCallable(functions, 'createSubmission');
      const response = await createSubmissionCallable({
        ...formData,
        sources: validSources.length > 0 ? validSources : undefined,
        uid: user?.uid,
        email: user?.email,
        ogData: ogData, // Include OG data in submission
        availableUntil: formData.availableUntil ? new Date(formData.availableUntil).toISOString() : null,
      });
      
      const data = response.data as { success: boolean; submissionId?: string; error?: string };

      if (data.success) {
        setSuccess(true);
        trackSubmission();
      } else {
        setError(data.error || 'Failed to submit. Please try again.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addSource = () => {
    setFormData({
      ...formData,
      sources: [...formData.sources, { label: '', url: '' }],
    });
  };

  const removeSource = (index: number) => {
    setFormData({
      ...formData,
      sources: formData.sources.filter((_, i) => i !== index),
    });
  };

  const updateSource = (index: number, field: 'label' | 'url', value: string) => {
    const newSources = [...formData.sources];
    newSources[index][field] = value;
    setFormData({ ...formData, sources: newSources });
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-primary-50 py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 text-center animate-fade-in">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h2>
            <p className="text-gray-700 mb-6">
              Your submission has been received and will be reviewed by our team shortly.
              We&apos;ll automatically check the eligibility to help speed up the review process.
              {!user && ' Consider creating an account to track your submissions.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/"
                className="inline-block bg-primary-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors"
              >
                Back to Home
              </a>
              {!user && (
                <a
                  href="/auth/signup"
                  className="inline-block border-2 border-primary-500 text-primary-600 px-6 py-3 rounded-xl font-semibold hover:bg-primary-50 transition-colors"
                >
                  Create Account
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-primary-50 py-16 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Submit an Eligible Item
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Help the community by sharing FSA/HSA eligible items you&apos;ve discovered.
            Your submission will be reviewed and added to our database.
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Product URL - Moved to top for OG data fetching */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Product URL (Recommended)
              </label>
              <input
                type="url"
                value={formData.productUrl}
                onChange={(e) => setFormData({ ...formData, productUrl: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none text-gray-900 placeholder-gray-500"
                placeholder="https://amazon.com/product..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the product URL and we&apos;ll auto-fill details from the page
              </p>
              {fetchingOg && (
                <div className="mt-2 flex items-center gap-2 text-sm text-primary-600">
                  <div className="animate-spin w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full"></div>
                  Fetching product details...
                </div>
              )}
            </div>

            {/* OG Preview */}
            {ogData && (ogData.ogImage || ogData.ogTitle) && (
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <p className="text-xs font-semibold text-gray-500 mb-2 uppercase">Preview</p>
                <div className="flex gap-4">
                  {ogData.ogImage && (
                    <img
                      src={ogData.ogImage}
                      alt="Product preview"
                      className="w-24 h-24 object-cover rounded-lg"
                      onError={(e) => (e.currentTarget.style.display = 'none')}
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    {ogData.ogTitle && (
                      <p className="font-semibold text-gray-900 truncate">{ogData.ogTitle}</p>
                    )}
                    {ogData.ogDescription && (
                      <p className="text-sm text-gray-600 line-clamp-2">{ogData.ogDescription}</p>
                    )}
                    {ogData.ogSiteName && (
                      <p className="text-xs text-gray-400 mt-1">{ogData.ogSiteName}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Item Title *
              </label>
              <input
                type="text"
                required
                minLength={3}
                maxLength={200}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none text-gray-900 placeholder-gray-500"
                placeholder="e.g., Sunscreen SPF 30+"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                required
                minLength={10}
                maxLength={1000}
                rows={4}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none text-gray-900 placeholder-gray-500"
                placeholder="Provide a detailed description of the item..."
              />
            </div>

            {/* Account Type */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Account Type *
              </label>
              <select
                value={formData.accountType}
                onChange={(e) =>
                  setFormData({ ...formData, accountType: e.target.value as 'FSA' | 'HSA' | 'Both' })
                }
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none bg-white text-gray-900"
              >
                <option value="FSA">FSA Only</option>
                <option value="HSA">HSA Only</option>
                <option value="Both">Both FSA & HSA</option>
              </select>
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Category (Optional)
              </label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none text-gray-900 placeholder-gray-500"
                placeholder="e.g., First Aid, Vision Care, Dental"
              />
            </div>

            {/* Reasoning */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Why is this item eligible? *
              </label>
              <textarea
                required
                minLength={10}
                maxLength={500}
                rows={3}
                value={formData.reasoning}
                onChange={(e) => setFormData({ ...formData, reasoning: e.target.value })}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none text-gray-900 placeholder-gray-500"
                placeholder="Explain the eligibility rationale..."
              />
            </div>

            {/* Availability Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Available Until (Optional)
              </label>
              <input
                type="date"
                value={formData.availableUntil}
                onChange={(e) => setFormData({ ...formData, availableUntil: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none text-gray-900"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave blank if the item is always available. Set a date for limited-time offers or deals.
              </p>
            </div>

            {/* Sources */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Sources (Optional)
              </label>
              {formData.sources.map((source, index) => (
                <div key={index} className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={source.label}
                    onChange={(e) => updateSource(index, 'label', e.target.value)}
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none text-gray-900 placeholder-gray-500"
                    placeholder="Source label"
                  />
                  <input
                    type="url"
                    value={source.url}
                    onChange={(e) => updateSource(index, 'url', e.target.value)}
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-primary-500 outline-none text-gray-900 placeholder-gray-500"
                    placeholder="https://..."
                  />
                  {formData.sources.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeSource(index)}
                      className="px-4 py-3 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-colors"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addSource}
                className="text-primary-600 hover:text-primary-700 font-medium text-sm"
              >
                + Add Another Source
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 text-white py-4 rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg"
            >
              {loading ? 'Submitting...' : 'Submit for Review'}
            </button>

            <p className="text-sm text-gray-600 text-center">
              By submitting, you agree that this information is accurate to the best of your
              knowledge. We&apos;ll auto-verify eligibility to help with the review.
            </p>
          </form>
        </div>

        {/* Why Submit Section */}
        <div className="mt-12 bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            Why Submit Items?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-4xl mb-2">🤝</div>
              <h3 className="font-semibold text-gray-900 mb-2">Help Others</h3>
              <p className="text-sm text-gray-700">
                Share your knowledge and help others make informed FSA/HSA decisions
              </p>
            </div>
            <div>
              <div className="text-4xl mb-2">⭐</div>
              <h3 className="font-semibold text-gray-900 mb-2">Build Reputation</h3>
              <p className="text-sm text-gray-700">
                Earn badges and recognition for quality contributions
              </p>
            </div>
            <div>
              <div className="text-4xl mb-2">📚</div>
              <h3 className="font-semibold text-gray-900 mb-2">Grow the Database</h3>
              <p className="text-sm text-gray-700">
                Contribute to the most comprehensive FSA/HSA eligibility resource
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SubmitContent() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gradient-to-b from-white to-primary-50 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    }>
      <SubmitFormContent />
    </Suspense>
  );
}
